import { useState, useEffect } from 'react';
import { hourmeterService } from '../services/api';
import type { Hourmeter, HourmeterDetail, HourmeterGroup } from '../types';
import { useAuth } from '../contexts/AuthContext';

export function useHourmeters() {
  const { operator } = useAuth();
  const [hourmeterGroups, setHourmeterGroups] = useState<HourmeterGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHourmeters = async () => {
    if (!operator) return;
    // TODO: Add loading state
    setLoading(true);
    setError(null);

    try {
      const userRole = operator.role || 'operator';
      const data = await hourmeterService.getOperatorHourmeters(operator.id, userRole);
      const validatedData = Array.isArray(data) ? data : [];
      
      setHourmeterGroups(validatedData);
    } catch (err) {
      console.error('Error fetching hourmeters:', err);
      setError('Error al cargar los horometros');
      setHourmeterGroups([]);
    } finally {
      setLoading(false);
    }
  };


  // Helper function to get all hourmeters flattened (for stats)
  const getAllHourmeters = (): Hourmeter[] => {
    // ✅ Defensive programming: check if hourmeterGroups is an array
    if (!Array.isArray(hourmeterGroups)) {
      return [];
    }
    return hourmeterGroups.flatMap(group => group.hourmeters);
  };

  useEffect(() => {
    fetchHourmeters();
  }, [operator]);

  return {
    hourmeterGroups,
    hourmeters: getAllHourmeters(), // For backward compatibility
    loading,
    error,
    refetch: fetchHourmeters,
  };
}

export function useHourmeterDetail(hourmeterId: number | null) {
  const { operator } = useAuth();
  const [hourmeter, setHourmeter] = useState<HourmeterDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHourmeter = async () => {
    if (!hourmeterId || !operator) return;

    setLoading(true);
    setError(null);

    try {
      const userRole = operator.role || 'operator';
      const data = await hourmeterService.getHourmeterForReceipt(hourmeterId, operator.id, userRole);
      setHourmeter(data);
    } catch (err) {
      console.error('Error fetching hourmeter detail:', err);
      setError('Error al cargar los detalles del horometro');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHourmeter();
  }, [hourmeterId, operator]);

  return {
    hourmeter,
    loading,
    error,
    refetch: fetchHourmeter,
  };
}
